/**
 * Mirrors `mre.schema.notification_events.UserNotification` — one event on
 * the signed-in user's personal stream (`notify:user:{id}:events`). Every
 * producer (chat, email, ...) publishes this same envelope and identifies
 * itself through `kind`; the UI keys on `kind` for presentation and on
 * `resource_type`/`resource_id` to show live status on lists of things.
 *
 * A second event with the same `id` is an update to the first (e.g. a
 * richer message for the same chat turn), never a new alert.
 */
export interface UserNotification {
  id: string;
  kind: string;
  created_at: string;
  title: string | null;
  message: string | null;
  /** Lifecycle word for the resource (working / completed / error / ...). */
  status: string | null;
  resource_type: string | null;
  resource_id: string | null;
  /** In-app route to open when clicked. */
  link: string | null;
  /** `TAB_ID` of the tab whose request started this; null when none did. */
  origin_tab?: string | null;
  data: Record<string, unknown>;
  /**
   * Set by the SSE router on entries it backfills from Redis to a fresh
   * subscriber: history, not news. Never present on a live event.
   */
  _replay?: boolean;
  /**
   * On a backfilled entry: true when no live subscriber of the user's ever
   * received it (it finished while they were signed out), so it is unread
   * history. Absent on older backends and on live events.
   */
  _unseen?: boolean;
}

/** A notification as held in the bell list. */
export interface StoredNotification extends UserNotification {
  read: boolean;
}

export interface ResourceStatus {
  status: string;
  updatedAt: string;
}

export const userEventStreamKey = (userId: number) =>
  `notify:user:${userId}:events`;

/** SSE event name every notification is published under. */
export const NOTIFICATION_EVENT = "notification";

/** Statuses that mean "still in progress" — the row shows a spinner and
 *  nothing lands in the bell. Everything else is terminal. */
const ACTIVE_STATUSES = new Set(["working", "running", "pending", "queued"]);
export const isActiveStatus = (status: string | null | undefined) =>
  !!status && ACTIVE_STATUSES.has(status);

/**
 * An active status with no update for this long is treated as idle: the
 * producer died (pod restart, lost publish) and no terminal event is coming.
 */
export const ACTIVE_STATUS_TTL_MS = 30 * 60 * 1000;

export const isStaleActive = (
  s: ResourceStatus | undefined,
  now = Date.now(),
): boolean =>
  !!s &&
  isActiveStatus(s.status) &&
  now - Date.parse(s.updatedAt) > ACTIVE_STATUS_TTL_MS;

export const resourceKey = (type: string, id: string | number) =>
  `${type}:${id}`;

/**
 * Shape check on what came off the wire. The envelope is typed on the
 * backend, but a producer bug or a version skew must not take the bell —
 * and with it the whole header — down.
 */
export function isUserNotification(data: unknown): data is UserNotification {
  if (!data || typeof data !== "object") return false;
  const n = data as Record<string, unknown>;
  return (
    typeof n.id === "string" &&
    n.id.length > 0 &&
    typeof n.kind === "string" &&
    n.kind.length > 0 &&
    typeof n.created_at === "string" &&
    Number.isFinite(Date.parse(n.created_at))
  );
}
